/**
 * DE: Was das Gerät über sich verrät; im Browser aus `navigator` und `matchMedia` gelesen.
 * EN: What the device tells about itself; read from `navigator` and `matchMedia` in the browser.
 */
export interface DeviceFacts {
  userAgent: string;
  maxTouchPoints: number;
  /** DE: Safari-eigenes `navigator.standalone`. EN: Safari's own `navigator.standalone`. */
  standalone: boolean;
  /** DE: `display-mode: standalone` trifft zu. EN: `display-mode: standalone` matches. */
  displayStandalone: boolean;
}

// DE: Chrome, Firefox und Edge auf dem iPhone haben ein anderes Teilen-Menü.
// EN: Chrome, Firefox and Edge on the iPhone have a different share menu.
const OTHER_IOS_BROWSERS = /CriOS|FxiOS|EdgiOS/;

/**
 * DE: Hinweis zeigen, wenn Safari auf iPhone oder iPad läuft und die App noch nicht auf dem
 * Home-Bildschirm liegt; iPadOS meldet sich als Mac mit Touch.
 * EN: Show the hint when Safari runs on an iPhone or iPad and the app is not on the home screen
 * yet; iPadOS reports itself as a Mac with touch.
 */
export function needsInstallHint(device: DeviceFacts): boolean {
  const { userAgent } = device;
  const appleMobile =
    /iPhone|iPad|iPod/.test(userAgent) ||
    (userAgent.includes('Macintosh') && device.maxTouchPoints > 1);
  const installed = device.standalone || device.displayStandalone;
  return appleMobile && !OTHER_IOS_BROWSERS.test(userAgent) && !installed;
}

/**
 * DE: Gerätedaten aus dem Browser lesen; fehlende Angaben gelten als „nein“.
 * EN: Read the device facts from the browser; missing values count as "no".
 */
export function readDeviceFacts(): DeviceFacts {
  const safari = navigator as Navigator & { standalone?: boolean };
  const query =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)')
      : undefined;
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
    standalone: safari.standalone === true,
    displayStandalone: query?.matches === true,
  };
}
